import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSubscriptions } from '../hooks/useSubscriptions'
import '../styles/subscriptions.scss'
import { FaPlus, FaTrash, FaSearch } from 'react-icons/fa'

export const Subscriptions = () => {
    const user = useSelector(state => state.userController.user)
    const navigate = useNavigate()
    const {
        subscriptions,
        isLoading,
        currentPage,
        totalPages,
        stats,
        fetchSubscriptions,
        fetchSubscriptionsByStatus,
        searchSubscriptions,
        deleteSubscription,
        updateSubscriptionStatus,
        updateSubscriptionUsage,
        createSubscription
    } = useSubscriptions()

    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [deleteLoading, setDeleteLoading] = useState('')
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [driverQuery, setDriverQuery] = useState('')
    const [driverResults, setDriverResults] = useState([])
    const [driverLoading, setDriverLoading] = useState(false)
    const [selectedDriver, setSelectedDriver] = useState(null)
    const [packages, setPackages] = useState([])
    const [packagesLoading, setPackagesLoading] = useState(false)
    const [selectedPackageId, setSelectedPackageId] = useState('')
    const [includeInProfit, setIncludeInProfit] = useState(true)
    const [createSubmitting, setCreateSubmitting] = useState(false)
    const [usageDrafts, setUsageDrafts] = useState({})
    const [usageLoading, setUsageLoading] = useState('')

    useEffect(() => {
        if (!user) {
            navigate('/login')
            return
        }
    }, [user, navigate])

    const selectedPackage = useMemo(
        () => packages.find((p) => p._id === selectedPackageId) || null,
        [packages, selectedPackageId]
    )

    const handleSearch = (e) => {
        e.preventDefault()
        if (searchTerm.trim()) {
            searchSubscriptions(searchTerm)
        } else {
            fetchSubscriptions()
        }
    }

    const handleStatusFilter = (status) => {
        setStatusFilter(status)
        if (status === 'all') {
            fetchSubscriptions()
        } else {
            fetchSubscriptionsByStatus(status)
        }
    }

    const handleDelete = async (subscriptionId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا الاشتراك؟')) {
            return
        }
        
        setDeleteLoading(subscriptionId)
        try {
            await deleteSubscription(subscriptionId)
        } catch (error) {
            console.error('Error deleting subscription:', error)
        } finally {
            setDeleteLoading('')
        }
    }

    const handleStatusChange = async (subscriptionId, newStatus) => {
        if (!window.confirm('هل أنت متأكد من إلغاء هذا الاشتراك؟')) {
            return
        }
        
        try {
            await updateSubscriptionStatus(subscriptionId, newStatus)
        } catch (error) {
            console.error('Error updating status:', error)
        }
    }

    const getStatusText = (status) => {
        const statusMap = {
            'active': 'نشط',
            'expired': 'منتهي',
            'cancelled': 'ملغي'
        }
        return statusMap[status] || status
    }

    const getRemainingDays = (endDate) => {
        const now = new Date()
        const end = new Date(endDate)
        const diff = end - now
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
        return days > 0 ? days : 0
    }

    const fetchPackages = useCallback(async () => {
        if (!user?.token) return
        setPackagesLoading(true)
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/packages`, {
                headers: { 'Authorization': `Bearer ${user.token}` }
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json.error)
            setPackages(json)
        } catch (err) {
            console.error('Failed to load packages', err)
        } finally {
            setPackagesLoading(false)
        }
    }, [user?.token])

    const searchDrivers = useCallback(async (query) => {
        if (!query || query.length < 3) {
            setDriverResults([])
            return
        }
        setDriverLoading(true)
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/subscription-drivers/search?phone=${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${user.token}` }
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json.error)
            setDriverResults(json)
        } catch (err) {
            console.error('Failed to search drivers', err)
        } finally {
            setDriverLoading(false)
        }
    }, [user?.token])

    useEffect(() => {
        if (showCreateModal) {
            fetchPackages()
        }
    }, [showCreateModal, fetchPackages])

    const handleDriverSearch = async (e) => {
        e.preventDefault()
        if (!driverQuery || driverQuery.trim().length < 3) {
            setDriverResults([])
            return
        }
        await searchDrivers(driverQuery.trim())
    }

    const handleCreateSubscription = async (e) => {
        e.preventDefault()
        if (!selectedDriver || !selectedPackage) return
        setCreateSubmitting(true)
        try {
            await createSubscription({
                entityType: 'driver',
                driverId: selectedDriver._id,
                driverName: selectedDriver.driverName,
                driverPhoneNumber: selectedDriver.phoneNumber,
                packageId: selectedPackage._id,
                includeInProfit,
            })
            setShowCreateModal(false)
            setSelectedDriver(null)
            setDriverQuery('')
            setSelectedPackageId('')
            setIncludeInProfit(true)
            fetchSubscriptions(currentPage)
        } catch (err) {
            // alert already handled in hook
        } finally {
            setCreateSubmitting(false)
        }
    }

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('ar-LY', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const getUsageDraft = (subscription) => {
        if (Object.prototype.hasOwnProperty.call(usageDrafts, subscription._id)) {
            return usageDrafts[subscription._id]
        }
        return String(subscription.completedTrips || 0)
    }

    const handleUsageSave = async (subscription) => {
        const raw = (usageDrafts[subscription._id] ?? String(subscription.completedTrips || 0)).trim()
        const parsed = Number(raw)
        if (!Number.isInteger(parsed) || parsed < 0) {
            return
        }

        setUsageLoading(subscription._id)
        try {
            const updated = await updateSubscriptionUsage(subscription._id, parsed)
            setUsageDrafts((prev) => ({ ...prev, [subscription._id]: String(updated.completedTrips || 0) }))
        } catch (error) {
            console.error('Error updating usage:', error)
        } finally {
            setUsageLoading('')
        }
    }

    if (isLoading) {
        return (
            <section className='container subscriptions'>
                <div className="loading">جاري التحميل...</div>
            </section>
        )
    }

    return (
        <section className='container subscriptions'>
            <div className="header">
                <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
                    <FaPlus />
                    <span>إنشاء اشتراك</span>
                </button>
            </div>

            {stats && (
                <div className="stats-cards">
                    <div className="stat-card">
                        <h3>إجمالي الاشتراكات</h3>
                        <p className="stat-value">{stats.total}</p>
                    </div>
                    <div className="stat-card active">
                        <h3>الاشتراكات النشطة</h3>
                        <p className="stat-value">{stats.active}</p>
                    </div>
                    <div className="stat-card expired">
                        <h3>الاشتراكات المنتهية</h3>
                        <p className="stat-value">{stats.expired}</p>
                    </div>
                    <div className="stat-card cancelled">
                        <h3>الاشتراكات الملغية</h3>
                        <p className="stat-value">{stats.cancelled}</p>
                    </div>
                </div>
            )}

            <div className="filters-section">
                <form className="search-form" onSubmit={handleSearch}>
                    <input
                        type="text"
                        placeholder="ابحث برقم الهاتف أو اسم المستخدم..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button type="submit">
                        <FaSearch />
                    </button>
                </form>

                <div className="status-filters">
                    <button 
                        className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                        onClick={() => handleStatusFilter('all')}
                    >
                        الكل
                    </button>
                    <button 
                        className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
                        onClick={() => handleStatusFilter('active')}
                    >
                        نشط
                    </button>
                    <button 
                        className={`filter-btn ${statusFilter === 'expired' ? 'active' : ''}`}
                        onClick={() => handleStatusFilter('expired')}
                    >
                        منتهي
                    </button>
                    <button 
                        className={`filter-btn ${statusFilter === 'cancelled' ? 'active' : ''}`}
                        onClick={() => handleStatusFilter('cancelled')}
                    >
                        ملغي
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table className="subscriptions-table">
                    <thead>
                        <tr>
                            <th>اسم المستخدم</th>
                            <th>رقم الهاتف</th>
                            <th>اسم الباقة</th>
                            <th>السعر</th>
                            <th>المدة</th>
                            <th>تاريخ البدء</th>
                            <th>تاريخ الانتهاء</th>
                            <th>الأيام المتبقية</th>
                            <th>الرحلات المستخدمة</th>
                            <th>الحالة</th>
                            <th>الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subscriptions.map(subscription => (
                            <tr key={subscription._id}>
                                <td>{subscription.driverName}</td>
                                <td dir='ltr'>{subscription.driverPhoneNumber}</td>
                                <td>{subscription.packageName}</td>
                                <td>
                                    <span className="price">{subscription.packagePrice} دينار</span>
                                </td>
                                <td>{subscription.packagePeriod} يوم</td>
                                <td>{formatDate(subscription.startDate)}</td>
                                <td>{formatDate(subscription.endDate)}</td>
                                <td>
                                    {subscription.status === 'active' ? (
                                        <span className="remaining-days">
                                            {getRemainingDays(subscription.endDate)} يوم
                                        </span>
                                    ) : '-'}
                                </td>
                                <td>
                                    <div className="usage-control">
                                        <input
                                            type="number"
                                            min="0"
                                            value={getUsageDraft(subscription)}
                                            onChange={(e) => setUsageDrafts((prev) => ({ ...prev, [subscription._id]: e.target.value }))}
                                        />
                                        <button
                                            className="save-usage-btn"
                                            onClick={() => handleUsageSave(subscription)}
                                            disabled={usageLoading === subscription._id}
                                        >
                                            {usageLoading === subscription._id ? '...' : 'حفظ'}
                                        </button>
                                    </div>
                                    {subscription.maxTrips ? (
                                        <small className="usage-limit">الحد: {subscription.maxTrips}</small>
                                    ) : (
                                        <small className="usage-limit">غير محدود</small>
                                    )}
                                </td>
                                <td>
                                    <span className={`status-badge ${subscription.status}`}>
                                        {getStatusText(subscription.status)}
                                    </span>
                                </td>
                                <td>
                                    <div className="actions">
                                        {subscription.status === 'active' && (
                                            <button 
                                                className="cancel-btn"
                                                onClick={() => handleStatusChange(subscription._id, 'cancelled')}
                                                title="إلغاء الاشتراك"
                                            >
                                                إلغاء
                                            </button>
                                        )}
                                        <button 
                                            className="delete-btn"
                                            onClick={() => handleDelete(subscription._id)}
                                            disabled={deleteLoading === subscription._id}
                                            title="حذف الاشتراك"
                                        >
                                            {deleteLoading === subscription._id ? (
                                                <span className="loading-spinner"></span>
                                            ) : (
                                                <FaTrash />
                                            )}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button 
                        onClick={() => fetchSubscriptions(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        السابق
                    </button>
                    <span>صفحة {currentPage} من {totalPages}</span>
                    <button 
                        onClick={() => fetchSubscriptions(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        التالي
                    </button>
                </div>
            )}

            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content tall-modal" onClick={e => e.stopPropagation()}>
                        <h2 className="TXT-heading2 color-normal">إنشاء اشتراك للسائق</h2>
                        <p className="modal-subtitle">اختر السائق ثم حدد الباقة المناسبة</p>
                        <form onSubmit={handleCreateSubscription} className="create-form">
                            <div className="dual">
                                <div className="input-group">
                                    <label>رقم هاتف السائق</label>
                                    <div className="search-inline">
                                        <input
                                            type="text"
                                            value={driverQuery}
                                            onChange={(e) => {
                                                setDriverQuery(e.target.value)
                                                setSelectedDriver(null)
                                            }}
                                            placeholder="أدخل رقم الهاتف ثم اضغط بحث"
                                        />
                                        <button type="button" onClick={handleDriverSearch} className="ghost-btn">
                                            <FaSearch />
                                            <span>بحث</span>
                                        </button>
                                    </div>
                                    {driverLoading && <small>جاري البحث...</small>}
                                    {driverResults.length > 0 && (
                                        <div className="select-list driver-cards">
                                            {driverResults.map(driver => (
                                                <div
                                                    key={driver._id}
                                                    className={`select-item driver-card ${selectedDriver?._id === driver._id ? 'active' : ''}`}
                                                    onClick={() => setSelectedDriver(driver)}
                                                >
                                                    <div className="avatar">
                                                        {driver.carImage ? (
                                                            <img src={driver.carImage} alt={driver.driverName} />
                                                        ) : (
                                                            <span>{driver.driverName?.charAt(0) || '?'}</span>
                                                        )}
                                                    </div>
                                                    <div className="info">
                                                        <strong>{driver.driverName}</strong>
                                                        <div className="muted">{driver.phoneNumber}</div>
                                                    </div>
                                                    <div className={`pill ${driver.isBanned ? 'danger' : 'neutral'}`}>
                                                        {driver.isBanned ? 'محظور' : driver.state}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="input-group">
                                    <label>الباقة</label>
                                    <select
                                        value={selectedPackageId}
                                        onChange={(e) => setSelectedPackageId(e.target.value)}
                                        disabled={packagesLoading}
                                    >
                                        <option value="">اختر الباقة</option>
                                        {packages.map(pkg => (
                                            <option key={pkg._id} value={pkg._id}>
                                                {pkg.name} - {pkg.isThereDiscount && pkg.priceAfterDiscount ? `${pkg.priceAfterDiscount} دينار (بدلاً من ${pkg.price})` : `${pkg.price} دينار`} / {pkg.durationInDays} يوم
                                            </option>
                                        ))}
                                    </select>
                                    {packagesLoading && <small>جاري تحميل الباقات...</small>}
                                </div>
                            </div>

                            <div className="selection-summary">
                                <div className="summary-card">
                                    <p className="label">السائق المحدد</p>
                                    {selectedDriver ? (
                                        <div className="value">{selectedDriver.driverName} - {selectedDriver.phoneNumber}</div>
                                    ) : (
                                        <div className="placeholder">لم يتم اختيار سائق بعد</div>
                                    )}
                                </div>
                                <div className="summary-card">
                                    <p className="label">الباقة المحددة</p>
                                    {selectedPackage ? (
                                        <div className="value">
                                            {selectedPackage.name} -
                                            {selectedPackage.isThereDiscount && selectedPackage.priceAfterDiscount ? (
                                                <>
                                                    <span className="price-discount"> {selectedPackage.priceAfterDiscount} دينار</span>
                                                    <span className="price-old"> {selectedPackage.price} دينار</span>
                                                </>
                                            ) : (
                                                <span> {selectedPackage.price} دينار</span>
                                            )}
                                            <span className="muted"> / {selectedPackage.durationInDays} يوم</span>
                                        </div>
                                    ) : (
                                        <div className="placeholder">لم يتم اختيار باقة بعد</div>
                                    )}
                                </div>
                            </div>

                            <div className="input-group checkbox-group inline">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={includeInProfit}
                                        onChange={(e) => setIncludeInProfit(e.target.checked)}
                                    />
                                    <span>احتساب هذا الاشتراك ضمن الأرباح</span>
                                </label>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={createSubmitting}
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={createSubmitting || !selectedDriver || !selectedPackage}
                                >
                                    {createSubmitting ? 'جاري الإنشاء...' : 'إنشاء الاشتراك'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    )
}

export default Subscriptions